import { type ConnectorInfo, type WorkflowNodeType } from '@blaze-shared/api-contracts-domain';
import { isApiCallNode, type Workflow, type WorkflowNode } from '@blaze-shared/workflow-domain';

export abstract class ConnectorProvider<T extends WorkflowNode> {
  getConnectors(workflow: Workflow, node: T, connectors: ConnectorInfo[]): ConnectorInfo[] {
    const nodeConnectors: ConnectorInfo[] = [];
    const parentNode = workflow.nodes.find((n) => n.nextNodeId === node.id);

    if (isApiCallNode(parentNode)) {
      const connector = connectors.find((c) => c.id === parentNode.connectorId);
      if (connector) {
        nodeConnectors.push(connector);
      }
    }

    return nodeConnectors;
  }
}

export type ConnectorProviderConstructor<T extends WorkflowNode> = new () => ConnectorProvider<T>;

export class ConnectorProviderRegistry {
  private static readonly registry = new Map<WorkflowNodeType, ConnectorProviderConstructor<any>>();

  static registerConnectorProvider<T extends WorkflowNode>(
    nodeType: WorkflowNodeType,
    schemaProviderClass: ConnectorProviderConstructor<T>,
  ): void {
    ConnectorProviderRegistry.registry.set(nodeType, schemaProviderClass);
  }

  static getConnectorProvider<T extends WorkflowNode>(
    nodeType: WorkflowNodeType,
  ): ConnectorProviderConstructor<T> {
    const connectorProviderClass = ConnectorProviderRegistry.registry.get(nodeType);

    if (!connectorProviderClass) {
      throw new Error(`No connector provider registered for ${nodeType}`);
    }

    return connectorProviderClass;
  }
}

export function WorkflowNodeConnectorProvider<T extends WorkflowNode>(
  nodeType: WorkflowNodeType,
): (connectorProviderClass: ConnectorProviderConstructor<T>) => void {
  return (schemaProviderClass: ConnectorProviderConstructor<T>): void => {
    ConnectorProviderRegistry.registerConnectorProvider(nodeType, schemaProviderClass);
  };
}
